import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  FocusEvent as ReactFocusEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from 'react';

/**
 * Anchors a chart's hover readout to the point it describes instead of to the
 * pointer, and makes it a surface a reader can aim at.
 *
 * **The problem this solves.** `ChartTooltip` used to be positioned by Recharts,
 * which moves it on every `mousemove` — it tracked the cursor across the plot.
 * That is fine for a readout and fatal for a readout with a control in it: the
 * button travels at exactly the speed of the pointer chasing it, so it can never
 * be reached. The workaround readers find on their own is to click first, which
 * happens to freeze the bubble; the mechanic was never designed, only
 * discovered. (User's call, 2026-09-24, choosing this over click-to-drill and
 * over a pinned-tooltip pattern.)
 *
 * **What anchoring means.** The readout is placed at the *datum* — the x-band's
 * own pixel on a cartesian plot, the sector's centroid on a donut. Moving the
 * pointer anywhere inside 08 Sep leaves it perfectly still; it relocates only
 * when the pointer crosses into 09 Sep. Recharts cannot do this itself, so this
 * hook takes over position, lifetime and the payload, and `<Tooltip>` is left
 * mounted for its cursor alone. See `ChartFrame`'s `hover` slot for where the
 * bubble is actually drawn — outside the SVG, in the plot's positioning context,
 * which is also how `CalendarHeatmap` and `WordCloud` have always drawn theirs.
 *
 * **Why anchoring alone is not enough.** Three further things, or the button is
 * still unclickable:
 *
 *  1. The bubble has to accept pointer events — but only once it is *locked*,
 *     or an unlocked readout parked over the plot would swallow the hover the
 *     chart needs. Unlocked it is `pointer-events: none` and the chart reads
 *     straight through it.
 *  2. It has to survive the pointer leaving the plot, because the last 8px of
 *     the journey to the button are outside the plot. Hence `GRACE_MS`.
 *  3. Band changes have to stop while the reader is aiming. Hence the lock.
 *
 * **Dwell to lock.** Anchoring on its own is a regression on a dense series:
 * Overview puts a 70-point daily series in a 384 card, where a band is ~5px and
 * a per-band anchor teleports every 5px of travel — jumpier than the glide it
 * replaced. So the readout follows bands while the pointer is moving and locks
 * once the pointer rests in one band for `DWELL_MS`; a click locks it at once,
 * which keeps working the reflex readers already built. A locked readout
 * releases when the pointer travels `RELEASE_RADIUS` from where it locked, so
 * decisive movement resumes scrubbing and a short move towards the button does
 * not.
 */

/** How long the pointer rests in one band before the readout locks. */
const DWELL_MS = 250;

/**
 * How long a locked readout outlives the pointer leaving the plot — long enough
 * to cross the `tooltipOffset` gap into the bubble, short enough that a readout
 * never lingers over a chart the reader has walked away from.
 */
const GRACE_MS = 200;

/**
 * How far the pointer travels from a locked point before the lock releases, in
 * px. Comfortably more than the 8px gap to the bubble, comfortably less than a
 * deliberate move to another part of the plot.
 */
const RELEASE_RADIUS = 48;

/**
 * Assumed bubble width the first time one is placed, before there is a rendered
 * one to measure. `ChartTooltip`'s own `max-width` — the widest it can be, so a
 * first placement errs towards flipping early rather than overflowing the card.
 * Every placement after that measures the real bubble.
 */
const ASSUMED_WIDTH = 296;

/** `chartGeometry.tooltipOffset`, repeated here to avoid a cycle through `chartTheme`. */
const OFFSET = 8;

/** Which side of the anchor the bubble sits on, once neither side is a given. */
export type AnchorSide = 'right' | 'left' | 'center';

export type AnchoredPoint = {
  /** Index into the chart's data array. */
  index: number;
  /** Plot-relative pixel the readout is pinned to. */
  x: number;
  y: number;
  side: AnchorSide;
  /** Above the point rather than below, where below would leave the plot. */
  flip: boolean;
  /**
   * How far the bubble is pulled back towards the middle of the plot, in px,
   * after the side and the flip have been decided — 0 whenever one of them
   * already left it fully inside.
   */
  shift: number;
};

export type AnchoredTooltipOptions = {
  /**
   * How many points the chart plots. The bound for keyboard stepping, and the
   * guard that keeps a stale index from outliving a data change.
   */
  count: number;
  /**
   * Opens the detail behind a point. Omitted, the readout is a pure readout:
   * still anchored, never interactive, never locked — there is nothing in it to
   * aim at.
   */
  onDrill?: (index: number) => void;
  /** Off while the chart draws no tooltip at all. */
  enabled?: boolean;
};

/** What Recharts hands a chart's mouse handlers, narrowed to what is used here. */
type ChartMouseState = {
  activeTooltipIndex?: number | string | null;
  activeCoordinate?: { x: number; y: number };
  isTooltipActive?: boolean;
};

/**
 * How long the exit runs, read from `--tt-out-dur` rather than repeated here.
 *
 * The readout has to stay mounted for exactly as long as its fade, and the fade
 * is CSS. Reading the token back is what keeps the two from drifting — and it
 * picks up `prefers-reduced-motion` for free, where `motion.css` collapses the
 * token to 0.01ms and the element unmounts on the next tick.
 */
function exitDuration(el: HTMLElement | null): number {
  if (!el) return 0;
  const raw = getComputedStyle(el).getPropertyValue('--tt-out-dur').trim();
  const ms = raw.endsWith('ms') ? Number.parseFloat(raw) : Number.parseFloat(raw) * 1000;
  return Number.isFinite(ms) ? ms : 0;
}

/** Recharts types the index as `number | string | null`; only a real row counts. */
function rowIndex(value: number | string | null | undefined, count: number): number | null {
  const index = typeof value === 'string' ? Number(value) : value;
  if (index === null || index === undefined || !Number.isInteger(index)) return null;
  return index >= 0 && index < count ? index : null;
}

export function useAnchoredTooltip({ count, onDrill, enabled = true }: AnchoredTooltipOptions) {
  const [point, setPoint] = useState<AnchoredPoint | null>(null);
  const [locked, setLocked] = useState(false);
  /** Mounted but leaving — the window the exit transition plays in. */
  const [exiting, setExiting] = useState(false);

  /** The plot box, for deciding which side of the anchor has room for a bubble. */
  const plotRef = useRef<HTMLDivElement | null>(null);
  /**
   * The rendered bubble. Measured on the *next* placement rather than this one:
   * reading it after render to correct the current placement would either cost a
   * second paint or show the bubble in the wrong place for a frame.
   */
  const surfaceRef = useRef<HTMLDivElement | null>(null);

  const dwell = useRef<ReturnType<typeof setTimeout> | null>(null);
  const grace = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Holds the readout mounted through its fade, then takes it down. */
  const exit = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Where the pointer was when the lock took, in client coordinates. */
  const lockOrigin = useRef<{ x: number; y: number } | null>(null);
  /** Set while the pointer is over the bubble, where no band change may land. */
  const overSurface = useRef(false);

  const interactive = onDrill !== undefined && enabled;

  const clearTimers = useCallback(() => {
    if (dwell.current) clearTimeout(dwell.current);
    if (grace.current) clearTimeout(grace.current);
    if (exit.current) clearTimeout(exit.current);
    dwell.current = null;
    grace.current = null;
    exit.current = null;
  }, []);

  /**
   * Start the exit, and take the readout down when it has played.
   *
   * `setLocked(false)` runs on the **first frame of the fade**, not at the end
   * of it. `.hoverAnchor[data-locked]` is what grants pointer events, so
   * clearing it here is what stops a fading bubble from sitting over the plot
   * eating clicks it no longer looks able to take.
   */
  const dismiss = useCallback(() => {
    if (dwell.current) clearTimeout(dwell.current);
    if (grace.current) clearTimeout(grace.current);
    dwell.current = null;
    grace.current = null;
    lockOrigin.current = null;
    overSurface.current = false;
    setLocked(false);

    /* Already leaving — restarting would stretch the fade, not replay it. */
    if (exit.current !== null) return;
    setExiting(true);
    exit.current = setTimeout(() => {
      exit.current = null;
      setExiting(false);
      setPoint(null);
    }, exitDuration(plotRef.current));
  }, []);

  /** A readout caught mid-exit comes back rather than finishing the fade. */
  const cancelExit = useCallback(() => {
    if (exit.current !== null) {
      clearTimeout(exit.current);
      exit.current = null;
    }
    setExiting(false);
  }, []);

  /* A data change can leave the anchored index pointing past the end. */
  useEffect(() => {
    setPoint((current) => (current && current.index >= count ? null : current));
  }, [count]);

  /* Timers must not outlive the chart. */
  useEffect(() => clearTimers, [clearTimers]);

  /** Escape closes a readout, the way it closes every other transient surface. */
  useEffect(() => {
    if (point === null) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [point, dismiss]);

  /**
   * Right of the anchor when the bubble fits there, left when it fits there
   * instead, centred over the plot when neither is true — which happens on the
   * 384 cards, where the bubble is nearly as wide as the plot. Below the point
   * unless that would run off the bottom, which is `CalendarHeatmap`'s rule for
   * the same decision.
   *
   * Clamped to the plot rather than to the card, which is what Recharts did
   * before this took the placement over. The card can afford the overhang —
   * it declines `overflow: hidden` precisely so a readout may leave it — but a
   * bubble that hangs into the legend reads as a misplaced one.
   */
  const placementFor = useCallback((x: number, y: number) => {
    const plot = plotRef.current;
    const plotWidth = plot?.clientWidth ?? 0;
    const plotHeight = plot?.clientHeight ?? 0;
    const width = surfaceRef.current?.offsetWidth || ASSUMED_WIDTH;
    const height = surfaceRef.current?.offsetHeight ?? 0;

    const side: AnchorSide =
      plotWidth === 0 || x + OFFSET + width <= plotWidth
        ? 'right'
        : x - OFFSET - width >= 0
          ? 'left'
          : 'center';

    /*
     * Flipping is a choice between two placements, not an escape from one: a
     * bubble taller than the space above the point is *worse* above it than
     * below, because the plot's top is also the card's content edge and the
     * readout loses its first line rather than its last. So it flips only when
     * above actually fits.
     */
    const measured = plotHeight > 0 && height > 0;
    const fitsBelow = !measured || y + OFFSET + height <= plotHeight;
    const fitsAbove = measured && y - OFFSET - height >= 0;
    const flip = !fitsBelow && fitsAbove;

    /*
     * And when neither side fits — a readout taller than its own plot, which is
     * every dense multi-series bubble in a 384 card — it is slid back inside
     * instead of being left to hang out of one end. Clamped at both ends, so
     * the correction can never push the bubble out of the edge it was not
     * overflowing.
     */
    let shift = 0;
    if (measured && !flip) {
      shift = Math.min(Math.max(0, y + OFFSET + height - plotHeight), Math.max(0, y + OFFSET));
    } else if (measured && flip) {
      shift = Math.min(
        Math.max(0, OFFSET + height - y),
        Math.max(0, plotHeight - (y - OFFSET)),
      );
    }

    return { side, flip, shift };
  }, []);

  /** Pin the readout to a row, cancelling whatever the last one had pending. */
  const anchorTo = useCallback(
    (index: number, x: number, y: number) => {
      setPoint({ index, x, y, ...placementFor(x, y) });
    },
    [placementFor],
  );

  /*
   * The first placement of any bubble is made against an *assumed* width and no
   * height at all, because there is no rendered bubble to measure until after
   * it is placed. This second pass runs once there is one, before paint, and
   * corrects the placement if the real box disagrees — which is what stopped
   * tall readouts being drawn off the bottom of short plots. It settles in one
   * step: the placement is derived from the anchor and the measured box, never
   * from where the previous placement put it.
   */
  useLayoutEffect(() => {
    if (point === null) return;
    const next = placementFor(point.x, point.y);
    if (next.side === point.side && next.flip === point.flip && next.shift === point.shift) return;
    setPoint((current) => (current === point ? { ...current, ...next } : current));
  }, [placementFor, point]);

  const lockAt = useCallback((clientX: number, clientY: number) => {
    lockOrigin.current = { x: clientX, y: clientY };
    setLocked(true);
  }, []);

  const onMouseMove = useCallback(
    (state: ChartMouseState, event: { clientX: number; clientY: number }) => {
      if (!enabled) return;

      if (grace.current) {
        clearTimeout(grace.current);
        grace.current = null;
      }
      cancelExit();

      /* While the pointer is on the bubble the chart has nothing to say. */
      if (overSurface.current) return;

      const index = rowIndex(state.activeTooltipIndex, count);
      const coordinate = state.activeCoordinate;
      if (index === null || !coordinate) return;

      if (locked && lockOrigin.current) {
        const travelled = Math.hypot(
          event.clientX - lockOrigin.current.x,
          event.clientY - lockOrigin.current.y,
        );
        /* Aiming at the button, not scrubbing — hold everything still. */
        if (travelled <= RELEASE_RADIUS) return;
        setLocked(false);
        lockOrigin.current = null;
      }

      /* Same band: leave the readout exactly where it is and let dwell run. */
      if (point?.index === index) {
        if (interactive && !locked && dwell.current === null) {
          const { clientX, clientY } = event;
          dwell.current = setTimeout(() => {
            dwell.current = null;
            lockAt(clientX, clientY);
          }, DWELL_MS);
        }
        return;
      }

      if (dwell.current) {
        clearTimeout(dwell.current);
        dwell.current = null;
      }
      anchorTo(index, coordinate.x, coordinate.y);

      if (interactive) {
        const { clientX, clientY } = event;
        dwell.current = setTimeout(() => {
          dwell.current = null;
          lockAt(clientX, clientY);
        }, DWELL_MS);
      }
    },
    [anchorTo, cancelExit, count, enabled, interactive, lockAt, locked, point?.index],
  );

  const onMouseLeave = useCallback(() => {
    if (dwell.current) {
      clearTimeout(dwell.current);
      dwell.current = null;
    }
    /*
     * A locked readout is one the reader may be walking towards, and the last
     * leg of that walk leaves the plot. An unlocked one is just a readout, and
     * closing it at the boundary is what every chart did before.
     */
    if (!locked) {
      dismiss();
      return;
    }
    if (grace.current) clearTimeout(grace.current);
    grace.current = setTimeout(dismiss, GRACE_MS);
  }, [dismiss, locked]);

  /**
   * Click locks at once. It is the reflex readers built against the old
   * behaviour — click, then the bubble holds still — kept working on purpose,
   * and it is also what a touch device sends.
   */
  const onClick = useCallback(
    (state: ChartMouseState, event: { clientX: number; clientY: number }) => {
      if (!interactive) return;
      const index = rowIndex(state.activeTooltipIndex, count);
      const coordinate = state.activeCoordinate;
      if (index === null || !coordinate) return;
      cancelExit();
      if (dwell.current) {
        clearTimeout(dwell.current);
        dwell.current = null;
      }
      anchorTo(index, coordinate.x, coordinate.y);
      lockAt(event.clientX, event.clientY);
    },
    [anchorTo, cancelExit, count, interactive, lockAt],
  );

  const drill = useCallback(() => {
    if (point === null) return;
    onDrill?.(point.index);
    dismiss();
  }, [dismiss, onDrill, point]);

  /**
   * Arrow keys step the anchored point, Enter opens it. The plot has no pointer
   * for a keyboard reader to anchor with, so stepping is the only way in — and
   * the readout is already a real surface with a real button by the time it
   * matters. Anchored to the band's own pixel via `getPosition` is not available
   * outside the chart, so a stepped point is placed proportionally across the
   * plot, which lands on the band for an evenly spaced axis and near it
   * otherwise.
   */
  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (!interactive || count === 0) return;

      const step = (next: number) => {
        event.preventDefault();
        cancelExit();
        const index = Math.max(0, Math.min(count - 1, next));
        const plot = plotRef.current;
        const width = plot?.clientWidth ?? 0;
        const height = plot?.clientHeight ?? 0;
        const x = count === 1 ? width / 2 : (width * index) / (count - 1);
        const y = height / 2;
        setPoint({ index, x, y, ...placementFor(x, y) });
        setLocked(true);
        lockOrigin.current = null;
      };

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          step(point === null ? 0 : point.index + 1);
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          step(point === null ? count - 1 : point.index - 1);
          break;
        case 'Enter':
        case ' ':
          if (point !== null) {
            event.preventDefault();
            drill();
          }
          break;
        default:
          break;
      }
    },
    [cancelExit, count, drill, interactive, placementFor, point],
  );

  return {
    /** The anchored point, or `null` while nothing is. */
    point,
    /** True once the readout accepts pointer events. */
    locked,
    /** Goes on `ChartFrame`'s `plotRef`, which is the positioning context. */
    plotRef,
    /**
     * Spread onto the Recharts chart element.
     *
     * **`accessibilityLayer: false`** turns off Recharts' own keyboard layer,
     * which is on by default in v3 and puts `role="application"` and
     * `tabIndex={0}` on the `<svg>` it draws. Two costs, both real: every chart
     * got a second tab stop inside a plot that is already one, and clicking a
     * chart focused that svg and painted the browser's default focus ring —
     * a 5px blue box around the plot, reported 2026-09-24.
     *
     * The ring was the UA's, not ours: `.plot` declares no outline, and the svg
     * did not even match `:focus-visible`. Suppressing the outline would have
     * hidden the ring and left the duplicate tab stop, so the layer goes
     * instead — `plotProps` below is this chart's keyboard entry, with the
     * accessible name `ChartFrame` puts on the same box.
     */
    chartProps: { onMouseMove, onMouseLeave, onClick, accessibilityLayer: false },
    /**
     * Spread onto Recharts' `<Tooltip>`, which is kept only for its cursor —
     * the crosshair or band highlight under the readout. Pinning it while
     * locked stops the cursor drifting to a band the readout is not about.
     */
    cursorProps: {
      active: locked || undefined,
      defaultIndex: locked && point ? point.index : undefined,
    },
    /** Spread onto `ChartFrame`'s `plotProps`, for keyboard stepping. */
    plotProps: interactive
      ? {
          tabIndex: 0,
          onKeyDown,
          /*
           * Only when focus leaves the plot *and everything in it*. The readout's
           * button lives inside the plot, so a bare `onBlur={dismiss}` tore the
           * readout down at the exact moment a keyboard reader tabbed to the one
           * control it exists to offer — the keyboard equivalent of the chase
           * this whole mechanic replaced. Caught by review rather than by the
           * browser check, which pressed Enter from the plot and never tabbed.
           */
          onBlur: (event: ReactFocusEvent<HTMLDivElement>) => {
            const next = event.relatedTarget;
            if (next instanceof Node && event.currentTarget.contains(next)) return;
            dismiss();
          },
        }
      : undefined,
    /** Spread onto the bubble's wrapper. */
    surfaceProps: point
      ? {
          ref: surfaceRef,
          style: {
            '--anchor-x': `${point.x}px`,
            '--anchor-y': `${point.y}px`,
            '--anchor-shift': `${point.shift}px`,
          } as CSSProperties,
          'data-side': point.side,
          'data-flip': point.flip || undefined,
          'data-locked': locked || undefined,
          'data-exiting': exiting || undefined,
          onMouseEnter: () => {
            if (!locked) return;
            overSurface.current = true;
            if (grace.current) {
              clearTimeout(grace.current);
              grace.current = null;
            }
          },
          onMouseLeave: () => {
            overSurface.current = false;
            if (grace.current) clearTimeout(grace.current);
            grace.current = setTimeout(dismiss, GRACE_MS);
          },
        }
      : undefined,
    /** Opens the detail behind the anchored point and closes the readout. */
    drill: onDrill ? drill : undefined,
    dismiss,
  };
}

export type AnchoredTooltip = ReturnType<typeof useAnchoredTooltip>;
